import { startAudioControls } from "./audio-controls.js";
import { startFuxiSettingsBar } from "./fuxi-settings-bar.js";
import { startFuxiSettingsUnsaved } from "./fuxi-settings-unsaved.js";
import { startFuxiZone } from "./fuxi-zone.js";
import { startSaveLiju } from "./save-liju.js";
import { startSaveWords } from "./save-word.js";
import type { SaveWordsOptions } from "./save-word-options.js";

/**
 * Binding this site's own controls again after htmx has replaced them.
 *
 * `/user/ci/`, `/user/liju/` and `/user/fuxi/settings/` are enhanced by htmx,
 * and the regions it swaps hold controls that are not htmx's: a save control
 * on every row of the list and on every saved sentence, each bound by
 * `save-word.ts` and `save-liju.ts` when the page loaded, the play control
 * beside a recorded sentence of a reader's own (`audio-controls.ts`), and the
 * time zone the browser offers on the settings form. htmx replaces those elements, so
 * what arrives has no listener on it, and a press falls back to posting the
 * form and fetching the whole page. That is not broken, since posting the form
 * is what the control does with no script at all, but it is a page reload in
 * the middle of a page that was enhanced to avoid one.
 *
 * The settings form loses more than a listener. The form is itself the region
 * a save replaces, so the note the zone is offered in arrives hidden and stays
 * hidden until something looks at it again.
 *
 * **This is in the site bundle rather than the htmx one**, and htmx is not
 * imported here. `htmx:after:process` is an ordinary bubbling event, so a
 * listener on the document hears it without holding the library, and the two
 * bundles stay apart: `main-htmx.ts` carries no code of this site's, and this
 * file costs the pages that never load htmx a listener that never fires.
 *
 * **`htmx:after:process` rather than `htmx:after:swap`.** The swap event is
 * dispatched on the element that made the request, and on `/user/ci/` that
 * element is inside the region being replaced: the tagging bar, the filter and
 * the paging are all in it. A detached element's event never reaches the
 * document. `htmx:after:process` is dispatched on the content htmx has just
 * taken in, which is what wants binding and is in the page by then.
 *
 * The event fires once on load as well, over the whole document, and the
 * binders are safe to call twice. Each keeps the controls it has bound and
 * skips them. See `startSaveWords`.
 */

/**
 * The event htmx dispatches over content it has just taken into the page.
 *
 * htmx 4 spells its events with colons, where htmx 2 wrote
 * `htmx:afterProcessNode`. The name is written out here rather than taken from
 * the library, this file deliberately not importing it.
 */
const processed = "htmx:after:process";

/**
 * Bind this site's controls again whenever htmx brings new markup in.
 * @param page - The page the bundle is running on.
 * @param options - What the save control was started with, so the second
 * binding is the same binding as the first.
 */
export function startHtmxRebinding(
  page: Document,
  options: SaveWordsOptions = {},
): void {
  page.addEventListener(processed, (event: Event) => {
    const within = event.target;

    if (!(within instanceof Element)) {
      return;
    }

    startSaveWords(page, globalThis.fetch, { ...options, within });
    startSaveLiju(page, globalThis.fetch, within);
    startFuxiZone(page, within);
    startFuxiSettingsUnsaved(within);
    startFuxiSettingsBar(page, within);
    startAudioControls(page, globalThis.location.pathname, within);
  });
}
